import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ABMELDUNG_EREIGNIS,
  BEDIENER_EREIGNIS,
  initialen,
  ladeAnmeldung,
  ladeBediener,
  setzeAbmeldeMelder,
  starteAbmeldeWaechter,
} from './bediener';
import { meldeWiderruf, starteWiderrufNachsendung } from './abmelde-nachsendung';

import { belegZeitlimit, KassenApi } from './api';
import { SpeicherGeraetezeuge } from './geraetezeuge';
import { OFFLINE_ERWEITERT } from './offline-schalter';
import { ANMELDUNG_UNGUELTIG_TEXT, AnmeldeWaechter, type AnmeldeZustand } from './geraeteanmeldung';
import { Belege } from './komponenten/Belege';
import { Bericht } from './komponenten/Bericht';
import { Einrichtung } from './komponenten/Einrichtung';
import { Lager } from './komponenten/Lager';
import { Termine } from './komponenten/Termine';
import type { Position } from './warenkorb';
import { Status } from './komponenten/Status';
import { Tagesabschluss } from './komponenten/Tagesabschluss';
import { Verkauf } from './komponenten/Verkauf';
import { kassenSperreFuerRetoure } from './retoure/entwurf';
import { Retoure } from './retoure/Retoure';
import { retoureReiter, retoureStufe1Aktiv } from './retoure/schalter';
import { ladeModule, leseModule, speichereModule, type Module } from './module';
import { Wiederholabruf } from './statusabruf';
import {
  aktuelleKennung,
  kennungAbgleichen,
  markiereZaehlerUnsicher,
  neueKennungAnfordern,
  vergebeBelegnummer,
  zaehlerUnsicher,
} from './belegnummern';
import { nimmVorgaengeWiederAuf } from './vorgang';
import { abgleichLauf } from './abgleich-lauf';
import {
  beiVeraltetemSpeicher,
  IndexedDbGeraeteSpeicher,
  KeineSichereSpeicherung,
  LokalerGeraeteSpeicher,
  pruefeSchreibfaehigkeit,
  SpeicherBlockiert,
  type GeraeteSpeicher,
} from './geraetespeicher';
import { erstelleBonDruck, erstelleLadeAmGeraet } from './bonweg';
import { findeGeraet, type BonQuelle } from './geraet';
import {
  GeraetePuffer,
  ladeBonkopf,
  speichereBonkopf,
  synchronisiere,
  abweisungsMeldung,
  syncMeldung as syncMeldungAus,
  type SyncMeldung,
  type Bonkopf,
  type OfflinePuffer,
} from './offline';
import {
  meldeSpeicherfehler,
  speicherfehler,
  starteSpeicherPersistenz,
  type PersistenzSteuerung,
  type SpeicherErgebnis,
  type SpeicherZustand,
} from './speicher-persistenz';
import { SpeicherHinweis, SpeicherPille } from './komponenten/SpeicherHinweis';
import { SyncMeldungKarte } from './komponenten/SyncMeldungKarte';
import { ladeNachsendeListe } from './bonierung';
import { starteHintergrund } from './hintergrund';
import { konfigAusStamm, speichereStammCache, stammCacheVersion } from './schnellwahl';
import { geraetestatusMeldung, sendeGeraetestatus, type StatusSendung } from './geraetestatus';
import { stammAblage } from './stamm-speicher';
import { alsArtikelstamm, SyncDienst, type SyncZustand } from './sync';

type Reiter = BasisReiter | 'retoure';
type BasisReiter =
  'verkauf' | 'termine' | 'lager' | 'belege' | 'bericht' | 'abschluss' | 'status' | 'einrichtung';

const KASSEN_ID_SCHLUESSEL = 'bilanx.kassenId';
const KASSENSCHLUESSEL = 'bilanx.kassenschluessel';

/** Basis-URL der API: produktiv aus VITE_API_URL, in der Entwicklung der Vite-Proxy /api. */
export function apiBasisUrl(): string {
  const konfiguriert: unknown = import.meta.env.VITE_API_URL;
  return typeof konfiguriert === 'string' && konfiguriert !== '' ? konfiguriert : '/api';
}

export function App() {
  /** N1: aktueller Verkaufsstopp für die API-Schicht (State folgt weiter unten). */
  const verkaufsstoppRef = useRef<string | null>(null);
  /** Geräteanmeldung: hält alle Abrufe an, sobald der Server den Kassenschlüssel ablehnt. */
  const anmeldung = useMemo(() => new AnmeldeWaechter(), []);
  const [anmeldeZustand, setAnmeldeZustand] = useState<AnmeldeZustand>(() => anmeldung.aktuell());
  useEffect(() => anmeldung.abonniere(setAnmeldeZustand), [anmeldung]);
  // N1.6: genau eine Speicherart je Installation – IndexedDB (Zähler, Vorgänge, Puffer in einer
  // Datenbank) bzw. ohne IndexedDB fest localStorage (nur online, kein Puffer).
  const geraeteSpeicher = useMemo<GeraeteSpeicher>(
    () =>
      'indexedDB' in window
        ? new IndexedDbGeraeteSpeicher(window.indexedDB)
        : new LokalerGeraeteSpeicher(localStorage),
    [],
  );
  const api = useMemo(
    () =>
      new KassenApi({
        basisUrl: apiBasisUrl(),
        // Kurzes Beleg-Zeitlimit nur mit Schalter (Codex-Nachprüfung 1), sonst Sicherheitslimit
        zeitlimits: { beleg: belegZeitlimit(OFFLINE_ERWEITERT) },
        token: () => localStorage.getItem(KASSENSCHLUESSEL),
        bedienerSitzung: () =>
          ladeAnmeldung(sessionStorage, localStorage.getItem(KASSEN_ID_SCHLUESSEL) ?? '')
            ?.sitzung ?? null,
        anmeldung,
        // N1: Verkaufsstopp gilt für jeden Kassierweg dieses Fensters (Beleg, Karte, Rückerstattung)
        verkaufsstopp: () => verkaufsstoppRef.current,
        // Server meldet veraltete Preise (P8/S-5): Stamm sofort abgleichen
        beiPreisVeraltet: () => {
          void syncRef.current?.anstossen('preisVeraltet');
        },
        // R-12 Ü-2: zuletzt bestätigter Beleg je Kasse, dauerhaft im Gerätespeicher
        zeuge: new SpeicherGeraetezeuge(geraeteSpeicher),
      }),
    [anmeldung, geraeteSpeicher],
  );
  const [kassenId, setKassenId] = useState(() => localStorage.getItem(KASSEN_ID_SCHLUESSEL) ?? '');
  /**
   * Automatische Synchronisierung (P8) – app-weit, unabhängig vom Reiter: Delta-Abgleich des
   * Artikelstamms, Ereigniskanal, 60-s-Prüfung, Prüfsumme. Die Schnellwahl folgt über das
   * Fensterereignis `bilanx:stamm`.
   */
  const syncRef = useRef<SyncDienst | null>(null);
  const sync = useMemo(() => {
    if (kassenId === '') {
      return null;
    }
    const ablage = stammAblage();
    const roh = `roh:${kassenId}`;
    return new SyncDienst({
      quelle: {
        delta: (seit, etag) => api.artikelstammDelta(kassenId, seit, etag),
        syncstand: () => api.syncstand(kassenId),
        pruefsumme: () => api.stammPruefsumme(kassenId),
        ereignisse: (f, signal) => api.ereignisse(kassenId, f, signal),
        voll: () => api.artikelstamm(kassenId),
        profil: () => api.schnellwahlProfil(kassenId),
      },
      anzeigeVersion: () => stammCacheVersion(localStorage, kassenId),
      ablage: {
        lies: () => ablage?.lies(roh) ?? Promise.resolve(undefined),
        schreibe: (r) => ablage?.schreibe(roh, r) ?? Promise.resolve(),
      },
      uebernehme: (r) => {
        speichereStammCache(
          localStorage,
          kassenId,
          { version: r.version, konfig: konfigAusStamm(alsArtikelstamm(r), r.profil ?? null) },
          ablage,
        );
        window.dispatchEvent(new CustomEvent('bilanx:stamm', { detail: { kassenId } }));
      },
      online: () => navigator.onLine,
    });
  }, [api, kassenId]);
  const [syncZustand, setSyncZustand] = useState<SyncZustand>({ art: 'start' });
  // Gerätestatus an den Server (P9), alle 60 s, ohne Geheimnisse – Betreiber und Kunde sehen
  // so Sync, Rückstände und Kundenanzeige je Kasse.
  const statusRef = useRef<{ sync: SyncZustand; offline: number; speicher?: SpeicherZustand }>({
    sync: syncZustand,
    offline: 0,
  });
  statusRef.current.sync = syncZustand;
  useEffect(() => {
    if (kassenId === '') {
      return;
    }
    const statusSendung: StatusSendung = { ohneQuittung: false };
    const melden = (): void => {
      if (!navigator.onLine || anmeldung.aktuell().art !== 'ok') {
        return;
      }
      const { sync: z, offline, speicher } = statusRef.current;
      // Ältere API (strenges Schema ohne Quittungsfelder): einmal ohne sie erneut senden
      void sendeGeraetestatus(
        (daten) => api.geraetestatus(kassenId, daten),
        geraetestatusMeldung({
          sync: z,
          offlineBelege: offline,
          kuechenbons: ladeNachsendeListe(localStorage, kassenId).length,
          stamm: syncRef.current?.stamm ?? null,
          ...(speicher === undefined
            ? {}
            : {
                speicher: {
                  ...speicher,
                  ...(speicherfehler().length === 0 ? {} : { schreibfehler: speicherfehler() }),
                },
              }),
        }),
        statusSendung,
      ).catch(() => undefined); // Status ist Hilfe, kein Betrieb
    };
    const start = setTimeout(melden, 5_000);
    const timer = setInterval(melden, 60_000);
    return () => {
      clearTimeout(start);
      clearInterval(timer);
    };
  }, [api, anmeldung, kassenId]);
  useEffect(() => {
    syncRef.current = sync;
    if (sync === null) {
      return;
    }
    const abmelden = sync.abonniere(setSyncZustand);
    void sync.starte();
    const online = (): void => {
      sync.wiederverbinden();
    };
    const sichtbar = (): void => {
      if (document.visibilityState === 'visible') {
        void sync.anstossen('sichtbar');
      }
    };
    window.addEventListener('online', online);
    document.addEventListener('visibilitychange', sichtbar);
    return () => {
      abmelden();
      sync.stoppe();
      window.removeEventListener('online', online);
      document.removeEventListener('visibilitychange', sichtbar);
      if (syncRef.current === sync) {
        syncRef.current = null;
      }
    };
  }, [sync]);
  const [reiter, setReiter] = useState<Reiter>(kassenId === '' ? 'einrichtung' : 'verkauf');
  const puffer = useMemo<OfflinePuffer>(
    () => new GeraetePuffer(geraeteSpeicher),
    [geraeteSpeicher],
  );
  /**
   * N1.12: Verkaufsstopp – ein älteres Fenster blockiert das Speicher-Upgrade, bzw. ein neueres
   * Fenster hat die Datenbank übernommen. Solange gesetzt, entsteht kein Beleg.
   */
  // Codex R2-4: bis die Speicherprüfung bestanden ist, wird nicht kassiert
  const [verkaufsstopp, setVerkaufsstopp] = useState<string | null>(
    'Gerätespeicher wird geprüft – bitte einen Moment warten',
  );
  verkaufsstoppRef.current = verkaufsstopp;
  useEffect(() => {
    beiVeraltetemSpeicher(() => {
      setVerkaufsstopp(
        'Die Kasse wurde in einem anderen Fenster aktualisiert – dieses Fenster schließen bzw. neu laden',
      );
    });
    let aktiv = true;
    // Codex R3-6: tatsächliche Schreibfähigkeit nachweisen – Datenbank öffnen genügt nicht.
    // Eine echte Schreibtransaktion im gewählten Speicher (IndexedDB bzw. Rückfall mit
    // Web-Lock-Anforderung); erst danach wird kassiert.
    const schreibprobe = (): Promise<void> =>
      pruefeSchreibfaehigkeit(geraeteSpeicher, 'indexedDB' in window ? window.indexedDB : null);
    const pruefe = (): void => {
      schreibprobe().then(
        () => {
          if (aktiv) {
            setVerkaufsstopp(null);
          }
        },
        (e: unknown) => {
          if (!aktiv) {
            return;
          }
          setVerkaufsstopp(
            e instanceof SpeicherBlockiert
              ? 'Andere Fenster dieser Kasse schließen – erst dann kann hier kassiert werden'
              : e instanceof KeineSichereSpeicherung
                ? e.message
                : `Gerätespeicher nicht beschreibbar (${e instanceof Error ? e.message : String(e)}) – kein Verkauf möglich`,
          );
          // Erneut versuchen (andere Fenster geschlossen, Speicher frei)
          setTimeout(pruefe, 5_000);
        },
      );
    };
    pruefe();
    return () => {
      aktiv = false;
    };
  }, [geraeteSpeicher]);
  // P1-10/N1.11: dauerhaften Speicher anfordern; gepuffert wird nur, wenn er gewährt ist.
  // Paket A4: nicht nur beim Start, sondern auch nach der ersten Bedienung, nach der
  // Installation als App und auf Knopfdruck (undefined = Ergebnis steht noch aus).
  const [speicherErgebnis, setSpeicherErgebnis] = useState<SpeicherErgebnis | undefined>(undefined);
  const speicherDauerhaft = speicherErgebnis === true;
  const persistenzRef = useRef<PersistenzSteuerung | null>(null);
  useEffect(() => {
    const steuerung = starteSpeicherPersistenz({
      verwaltung: navigator.storage,
      ereignisse: window,
      onErgebnis: (dauerhaft) => {
        setSpeicherErgebnis(dauerhaft);
        statusRef.current.speicher = {
          dauerhaft,
          puffer: puffer.dauerhaft ? 'indexeddb' : 'fluechtig',
        };
      },
    });
    persistenzRef.current = steuerung;
    return () => {
      steuerung.stoppe();
      if (persistenzRef.current === steuerung) {
        persistenzRef.current = null;
      }
    };
  }, [puffer]);
  const speicherAnfordern = useCallback(
    (): Promise<SpeicherErgebnis> =>
      persistenzRef.current?.anfordern() ?? Promise.resolve(speicherErgebnis ?? null),
    [speicherErgebnis],
  );
  const [offlineAnzahl, setOfflineAnzahl] = useState(0);
  /** N1: gepufferte Verkäufe in Klärung im Backoffice. */
  const [klaerungAnzahl, setKlaerungAnzahl] = useState(0);
  /** N1 (Codex R2-3): gesendete Verkäufe, deren Ausgang noch nicht feststeht. */
  const [ungeklaert, setUngeklaert] = useState(0);
  statusRef.current.offline = offlineAnzahl;
  // Angemeldeten Bediener nach 15 Minuten ohne Bedienung abmelden (unbeaufsichtigtes Gerät).
  useEffect(
    () => (kassenId === '' ? undefined : starteAbmeldeWaechter(sessionStorage, kassenId)),
    [kassenId],
  );
  // PB-3: jedes Abmelden (Taste, Wechsel, Leerlauf) widerruft das Kennzeichen auch am Server;
  // scheitert der Widerruf (Netz, Server), bleibt er vorgemerkt und wird nachgesendet
  // (App-Start, „online“, Intervall), bis der Server ihn bestätigt.
  useEffect(() => {
    const abmelden = (k: string, sitzung: string) => api.bedienerAbmelden(k, sitzung);
    const melderWeg = setzeAbmeldeMelder((k, sitzung) => {
      void meldeWiderruf(abmelden, localStorage, k, sitzung);
    });
    const nachsendungStopp =
      kassenId === '' ? undefined : starteWiderrufNachsendung(abmelden, localStorage, kassenId);
    return () => {
      melderWeg();
      nachsendungStopp?.();
    };
  }, [api, kassenId]);
  const [syncMeldung, setSyncMeldung] = useState<SyncMeldung | null>(null);
  // P1-3: Bonkopf aus dem letzten Status – auch nach einem Neustart ohne Server.
  const [unternehmer, setUnternehmer] = useState<Bonkopf | null>(() =>
    kassenId === '' ? null : ladeBonkopf(localStorage, kassenId),
  );
  const [bridgeZugelassen, setBridgeZugelassen] = useState(false);
  const [zahlungsanbieter, setZahlungsanbieter] = useState<string | null>(null);
  const [monatsbelegFehltFuer, setMonatsbelegFehltFuer] = useState<string | null>(null);
  /** Bediener-Chip im Kopf angetippt: der Verkauf öffnet die Bedienerauswahl. */
  const [bedienerWahl, setBedienerWahl] = useState(false);
  /** Aus dem Terminbuch übernommene Positionen für den nächsten Bon. */
  const [vorbelegung, setVorbelegung] = useState<Position[] | null>(null);
  /** Gebuchte Module des Betriebs; offline der zuletzt bekannte Stand. */
  const [module, setModule] = useState<Module>(() => ladeModule(localStorage, kassenId));
  // Lager-Warteschlange und Küchenbon-Nachsendung app-weit (P8), nicht nur im offenen Reiter
  useEffect(
    () =>
      kassenId === ''
        ? undefined
        : starteHintergrund({
            api,
            kassenId,
            lager: module.lager,
            kuechenbons: module.kuechenbons,
          }),
    [api, kassenId, module.lager, module.kuechenbons],
  );
  /** Native Geräte-Bridge der Android-Hülle (SUNMI), sonst null. Einmal je Seitenladen. */
  const geraet = useMemo(() => findeGeraet(), []);
  /**
   * Reine Bonquelle (GET …/bonmodell, Server-Wunsch S-1). Bis S-1 null: Der Bon läuft weiter über
   * Server + Hardware-Bridge, nie zusätzlich nativ. Mit S-1:
   * `bonQuelleUeberBonmodellAbruf((pfad) => api.holeJson(pfad))`.
   */
  const bonQuelle: BonQuelle | null = null;
  /** Bondruck – genau ein Weg je Bon (nativ oder Hardware-Bridge, `bonweg.ts`). */
  const bridgeDruck = useMemo(
    () =>
      erstelleBonDruck({
        api,
        speicher: localStorage,
        kassenId,
        bridgeZugelassen,
        geraet,
        bonQuelle,
      }),
    [api, bridgeZugelassen, kassenId, geraet, bonQuelle],
  );
  /** Kassenlade über die native Bridge; undefined → heutiger Server-Weg. */
  const ladeAmGeraet = useMemo(() => erstelleLadeAmGeraet(geraet), [geraet]);

  /** Belegversand per E-Mail (Server braucht SMTP; sonst meldet die API 501). */
  const emailSenden = useMemo(
    () =>
      kassenId === ''
        ? undefined
        : (belegnummer: string, an: string, form: 'bon' | 'rechnung' = 'bon') =>
            api
              .belegEmail(kassenId, belegnummer, an, form)
              .then(
                (r) =>
                  `${form === 'rechnung' ? 'Rechnung' : 'Beleg'} ${belegnummer} an ${r.an} gesendet`,
              ),
    [api, kassenId],
  );

  const zaehleOffline = useCallback(async () => {
    if (kassenId === '') {
      return;
    }
    const anzahl = (await puffer.alle(kassenId)).length;
    setOfflineAnzahl(anzahl);
    if (anzahl === 0) {
      setKlaerungAnzahl(0);
    }
  }, [kassenId, puffer]);

  /**
   * A5-3: Zähler, den der Verkauf beobachtet – steigt, wenn ein Abgleich Belege nachsigniert oder
   * keine ungeklärten Verkäufe mehr findet (dann prüft der Verkauf seine „noch ungeklärt“-Meldung).
   */
  const [abgleichNr, setAbgleichNr] = useState(0);
  const letzteUngeklaert = useRef<number | null>(null);

  /**
   * Gepufferte Belege nachsynchronisieren – beim Start, bei Wiederverbindung und alle 30 s.
   * A5-2: ohne Verbindung wird nur der Puffer gezählt (Kopf nach einem Offline-Neustart).
   */
  const synchronisieren = useCallback(async () => {
    if (kassenId === '') {
      return;
    }
    // N1.10: Gerätekennung beim Server registrieren; bei unsicherem Zählerstand eine neue
    // anfordern (neue Nummernepoche). Scheitert das, bleibt es beim nächsten Lauf offen.
    const kennungPruefen = async (): Promise<void> => {
      try {
        if (await zaehlerUnsicher(geraeteSpeicher, kassenId)) {
          await neueKennungAnfordern(geraeteSpeicher, localStorage, api, kassenId);
        } else {
          await kennungAbgleichen(geraeteSpeicher, localStorage, api, kassenId);
        }
      } catch {
        // Ohne registrierte Kennung puffert die Kasse nicht (Nummernvergabe meldet das)
      }
    };
    const uebertrage = async (): Promise<number> => {
      const e = await synchronisiere(api, puffer, kassenId, new Date(), {
        merker: localStorage,
      });
      if (e !== null) {
        setKlaerungAnzahl(e.inKlaerung.length);
        setSyncMeldung(syncMeldungAus(e));
      }
      return e?.synchronisiert.length ?? 0;
    };
    // Codex R2-3: Wiederaufnahme aus den dauerhaften Vorgangsdatensätzen selbst (nicht aus
    // dem Bon-Merker, den ein anderes Fenster leeren kann): Jeder Verkauf, dessen erster
    // Sendeversuch sicher vorbei ist, wird mit derselben Vorgangs-ID wiederholt – vorhanden
    // oder jetzt gebucht, nie doppelt; unklare bleiben gespeichert.
    // Bezahlt bestätigte Verkäufe mit vergebener Nummer erhalten auch im Hintergrund ihren
    // Ersatz; ein gestörter Nummernkreis fordert eine neue Gerätekennung an (Codex-Prüfung 3)
    const wiederaufnahme = (): Promise<number> =>
      nimmVorgaengeWiederAuf(
        api,
        geraeteSpeicher,
        kassenId,
        Date.now,
        () => vergebeBelegnummer(geraeteSpeicher, localStorage, kassenId),
        {
          kennung: () => aktuelleKennung(geraeteSpeicher, localStorage),
          stoerung: () => markiereZaehlerUnsicher(geraeteSpeicher, kassenId),
        },
      );
    let ersteWiederaufnahme = true;
    const r = await abgleichLauf({
      online: () => navigator.onLine,
      wiederaufnahme: async () => {
        if (ersteWiederaufnahme) {
          ersteWiederaufnahme = false;
          await kennungPruefen();
        }
        return wiederaufnahme();
      },
      uebertrage,
      zaehlePuffer: zaehleOffline,
      setzeUngeklaert: setUngeklaert,
      meldeGrund: (text) => {
        setSyncMeldung(abweisungsMeldung(text));
      },
    });
    const vorher = letzteUngeklaert.current;
    if (r.ungeklaert !== null) {
      letzteUngeklaert.current = r.ungeklaert;
    }
    if (r.nachsigniert > 0 || (r.ungeklaert === 0 && vorher !== 0)) {
      setAbgleichNr((n) => n + 1);
    }
  }, [api, geraeteSpeicher, kassenId, puffer, zaehleOffline]);

  useEffect(() => {
    void synchronisieren();
    const intervall = setInterval(() => {
      void synchronisieren();
    }, 30_000);
    const beiOnline = (): void => {
      void synchronisieren();
    };
    window.addEventListener('online', beiOnline);
    return () => {
      clearInterval(intervall);
      window.removeEventListener('online', beiOnline);
    };
  }, [synchronisieren]);

  useEffect(() => {
    if (kassenId === '') {
      return;
    }
    // Codex-Nachprüfung PB-13 H-4: Scheitert der Abruf, wird er mit Backoff wiederholt und bei
    // Wiederverbindung erneut gestartet – sonst bliebe eine Kasse ohne gespeicherten
    // Modulstand nach einem einzigen Fehlschlag dauerhaft Grundkasse.
    const abruf = new Wiederholabruf(
      () => api.status(kassenId),
      (s) => {
        setUnternehmer(s.unternehmer);
        meldeSpeicherfehler('bonkopf', !speichereBonkopf(localStorage, kassenId, s.unternehmer));
        setBridgeZugelassen(s.bridgeZugelassen);
        setZahlungsanbieter(s.zahlungsanbieter);
        setMonatsbelegFehltFuer(s.pflichtbelege.monatsbelegFehltFuer);
        const m = leseModule(s.module);
        if (m !== null) {
          setModule(m);
          speichereModule(localStorage, kassenId, m);
        }
      },
    );
    abruf.starte();
    const beiOnline = (): void => {
      abruf.beiWiederverbindung();
    };
    window.addEventListener('online', beiOnline);
    return () => {
      window.removeEventListener('online', beiOnline);
      abruf.beenden();
    };
  }, [api, kassenId]);

  function waehleKasse(id: string): void {
    localStorage.setItem(KASSEN_ID_SCHLUESSEL, id);
    setKassenId(id);
    setModule(ladeModule(localStorage, id));
    setUnternehmer(ladeBonkopf(localStorage, id));
  }

  function speichereSchluessel(schluessel: string): void {
    if (schluessel === '') {
      localStorage.removeItem(KASSENSCHLUESSEL);
    } else {
      localStorage.setItem(KASSENSCHLUESSEL, schluessel);
      // Paket A4: eingerichtete Kasse – dauerhaften Speicher erneut anfordern
      void persistenzRef.current?.anfordern();
    }
  }

  const reiterListe: { id: Reiter; text: string }[] = [
    { id: 'verkauf', text: 'Verkauf' },
    ...retoureReiter(localStorage), // Retoure Stufe 1: nur mit Schalter, Vorgabe aus
    ...(module.termine ? [{ id: 'termine' as const, text: 'Termine' }] : []),
    ...(module.lager ? [{ id: 'lager' as const, text: 'Lager' }] : []),
    { id: 'belege', text: 'Belege' },
    { id: 'bericht', text: 'Bericht' },
    { id: 'abschluss', text: 'Abschluss' },
    { id: 'status', text: 'Status' },
    { id: 'einrichtung', text: 'Einrichtung' },
  ];

  return (
    <>
      <header className="kopf">
        <span className="marke" aria-hidden="true">
          B
        </span>
        <h1 className="unsichtbar">Bilanx Kassa</h1>
        <nav className="reiter" aria-label="Bereiche">
          {reiterListe.map((r) => (
            <button
              key={r.id}
              type="button"
              className={reiter === r.id ? 'aktiv' : ''}
              aria-current={reiter === r.id ? 'page' : undefined}
              disabled={r.id !== 'einrichtung' && kassenId === ''}
              onClick={() => {
                setReiter(r.id);
              }}
            >
              {r.text}
            </button>
          ))}
        </nav>
        <div className="kopf-status">
          <KopfVerbindung sync={syncZustand} />
          {anmeldeZustand.art !== 'ok' && (
            <span className="status-pille warnung">
              {anmeldeZustand.art === 'ungueltig' ? 'Anmeldung erforderlich' : 'Server-Sperre'}
            </span>
          )}
          {offlineAnzahl > 0 && (
            <span className="status-pille warnung">{offlineAnzahl} Beleg(e) offline gepuffert</span>
          )}
          {ungeklaert > 0 && (
            // Fehlerbericht 25.10.2026: eindeutig – nicht neu kassieren, erst den Status prüfen
            <span className="status-pille warnung" role="alert">
              {ungeklaert > 1 ? `${String(ungeklaert)} Verkäufe` : 'Verkauf'} noch ungeklärt. Bitte
              diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.
            </span>
          )}
          {klaerungAnzahl > 0 && (
            <span className="status-pille warnung">
              {klaerungAnzahl} Beleg(e) in Klärung im Backoffice
            </span>
          )}
          {verkaufsstopp !== null && <span className="status-pille warnung">Kein Verkauf</span>}
          {kassenId !== '' && <SpeicherPille dauerhaft={speicherErgebnis} />}
          <span className="kopf-kasse">
            <KopfUhr />
            <small>{kassenId === '' ? 'keine Kasse gewählt' : `Kasse ${kassenId}`}</small>
          </span>
          {kassenId !== '' && (
            <KopfBediener
              kassenId={kassenId}
              onWahl={() => {
                setReiter('verkauf');
                setBedienerWahl(true);
              }}
            />
          )}
        </div>
      </header>
      {anmeldeZustand.art === 'ungueltig' && kassenId !== '' && (
        <div
          className="karte fehler"
          role="alert"
          style={{ margin: '0.5rem 1rem 0', padding: '0.5rem 1rem' }}
        >
          {ANMELDUNG_UNGUELTIG_TEXT}{' '}
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              setReiter('einrichtung');
            }}
          >
            Zur Einrichtung
          </button>{' '}
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              // Genau ein neuer Versuch mit dem gespeicherten Schlüssel – scheitert er, bleibt die Sperre.
              anmeldung.erneutPruefen();
              api
                .status(kassenId)
                .then(() => {
                  void synchronisieren();
                  syncRef.current?.wiederverbinden(); // angehaltene Stammdaten-Synchronisierung
                })
                .catch(() => undefined);
            }}
          >
            Erneut prüfen
          </button>
        </div>
      )}
      {verkaufsstopp !== null && kassenId !== '' && (
        <div
          className="karte fehler"
          role="alert"
          style={{ margin: '0.5rem 1rem 0', padding: '0.5rem 1rem' }}
        >
          {verkaufsstopp}
        </div>
      )}
      {kassenId !== '' && (
        <SpeicherHinweis
          dauerhaft={speicherErgebnis}
          onAnfordern={speicherAnfordern}
          sitzung={sitzungsspeicher()}
        />
      )}
      {syncMeldung !== null && (
        <SyncMeldungKarte
          meldung={syncMeldung}
          onSchliessen={() => {
            setSyncMeldung(null);
          }}
        />
      )}
      {/* Alle Reiter außer dem Touch-Verkauf in der kompakten Verwaltungsansicht (wie das Backoffice). */}
      <main className={reiter === 'verkauf' ? 'verkauf' : 'verwaltung'}>
        {reiter === 'verkauf' && kassenId !== '' && (
          <Verkauf
            api={api}
            kassenId={kassenId}
            {...(sync === null ? {} : { sync })}
            puffer={puffer}
            geraeteSpeicher={geraeteSpeicher}
            verkaufsstopp={verkaufsstopp}
            speicherDauerhaft={speicherDauerhaft}
            unternehmer={unternehmer}
            module={module}
            {...(vorbelegung === null ? {} : { vorbelegung })}
            onVorbelegungUebernommen={() => {
              setVorbelegung(null);
            }}
            onOffline={() => {
              void zaehleOffline();
            }}
            abgleichNr={abgleichNr}
            {...(bridgeDruck === undefined ? {} : { bridgeDruck })}
            {...(ladeAmGeraet === undefined ? {} : { ladeAmGeraet })}
            {...(emailSenden === undefined ? {} : { emailSenden })}
            bedienerWahl={bedienerWahl}
            onBedienerWahlGezeigt={() => {
              setBedienerWahl(false);
            }}
          />
        )}
        {reiter === 'retoure' && kassenId !== '' && retoureStufe1Aktiv(localStorage) && (
          <Retoure
            api={api}
            kassenId={kassenId}
            kassenSperre={kassenSperreFuerRetoure({
              anmeldung: anmeldeZustand.art,
              verkaufsstopp,
              offlineGepuffert: offlineAnzahl,
              ungeklaert,
            })}
          />
        )}
        {reiter === 'termine' && kassenId !== '' && module.termine && (
          <Termine
            api={api}
            kassenId={kassenId}
            onKassieren={(positionen) => {
              setVorbelegung(positionen);
              setReiter('verkauf');
            }}
          />
        )}
        {reiter === 'lager' && kassenId !== '' && module.lager && (
          <Lager api={api} kassenId={kassenId} />
        )}
        {reiter === 'belege' && kassenId !== '' && (
          <Belege
            api={api}
            kassenId={kassenId}
            unternehmer={unternehmer}
            {...(bridgeDruck === undefined ? {} : { bridgeDruck })}
            {...(emailSenden === undefined ? {} : { emailSenden })}
          />
        )}
        {reiter === 'bericht' && kassenId !== '' && (
          <Bericht api={api} kassenId={kassenId} bridgeDruck={bridgeZugelassen} />
        )}
        {reiter === 'abschluss' && kassenId !== '' && (
          <Tagesabschluss
            api={api}
            kassenId={kassenId}
            zahlungsanbieter={zahlungsanbieter}
            bridgeZugelassen={bridgeZugelassen}
            monatsbelegFehltFuer={monatsbelegFehltFuer}
            geraeteSpeicher={geraeteSpeicher}
          />
        )}
        {reiter === 'status' && kassenId !== '' && (
          <Status
            api={api}
            kassenId={kassenId}
            {...(bridgeDruck === undefined ? {} : { bridgeDruck })}
            {...(emailSenden === undefined ? {} : { emailSenden })}
          />
        )}
        {reiter === 'einrichtung' && (
          <Einrichtung
            api={api}
            basisUrl={apiBasisUrl()}
            kassenId={kassenId}
            kassenschluessel={localStorage.getItem(KASSENSCHLUESSEL) ?? ''}
            kundenanzeige={module.kundenanzeige}
            kuechenbons={module.kuechenbons}
            bridgeZugelassen={bridgeZugelassen}
            onKasse={waehleKasse}
            onSchluessel={speichereSchluessel}
          />
        )}
      </main>
    </>
  );
}

/** sessionStorage, falls verfügbar (manche Browser werfen beim Zugriff). */
function sitzungsspeicher(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Datum und Uhrzeit (Wiener Zeit) im Kopf, minütlich aktualisiert. */
function KopfUhr() {
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setJetzt(new Date());
    }, 15_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return (
    <span className="kopf-zeit">
      {jetzt.toLocaleDateString('de-AT', {
        timeZone: 'Europe/Vienna',
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })}
      {' · '}
      {jetzt.toLocaleTimeString('de-AT', {
        timeZone: 'Europe/Vienna',
        hour: '2-digit',
        minute: '2-digit',
      })}
    </span>
  );
}

/** Verbindungsstatus im Kopf (Browser online/offline). */
/** Sync-Zustand als Text (Hinweis an der bestehenden Verbindungsanzeige; Darstellung folgt Figma). */
export function syncText(z: SyncZustand): string {
  switch (z.art) {
    case 'start':
      return 'Synchronisierung startet';
    case 'synchron':
      return `Stammdaten synchron (${new Date(z.am).toLocaleTimeString('de-AT')})`;
    case 'aktualisiert':
      return `Stammdaten aktualisiert (${new Date(z.am).toLocaleTimeString('de-AT')})`;
    case 'offline':
      return z.lokalerStand
        ? 'Offline – letzter Stand der Stammdaten'
        : 'Offline – keine Stammdaten';
    case 'fehler':
      return `Synchronisierung gestört: ${z.grund}`;
    case 'anmeldungUngueltig':
      return 'Synchronisierung angehalten – Anmeldung erforderlich';
  }
}

function KopfVerbindung(props: { sync: SyncZustand }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const an = (): void => {
      setOnline(true);
    };
    const aus = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', an);
    window.addEventListener('offline', aus);
    return () => {
      window.removeEventListener('online', an);
      window.removeEventListener('offline', aus);
    };
  }, []);
  return (
    <span
      className={`status-pille ${online ? 'online' : 'offline'}`}
      role="status"
      title={syncText(props.sync)}
      data-sync={props.sync.art}
    >
      <i aria-hidden="true" />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

/**
 * Angemeldeter bzw. gewählter Bediener mit Initialen im Kopf. Antippen öffnet im Verkauf
 * die bestehende Bedienerauswahl (Anmelden/Abmelden bzw. Wechsel).
 */
function KopfBediener(props: { kassenId: string; onWahl: () => void }) {
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    const aktualisiere = (): void => {
      setName(
        ladeAnmeldung(sessionStorage, props.kassenId)?.name ??
          ladeBediener(localStorage, props.kassenId).aktiv,
      );
    };
    aktualisiere();
    const timer = setInterval(aktualisiere, 30_000); // Änderungen aus anderen Tabs
    window.addEventListener(ABMELDUNG_EREIGNIS, aktualisiere);
    window.addEventListener(BEDIENER_EREIGNIS, aktualisiere);
    return () => {
      clearInterval(timer);
      window.removeEventListener(ABMELDUNG_EREIGNIS, aktualisiere);
      window.removeEventListener(BEDIENER_EREIGNIS, aktualisiere);
    };
  }, [props.kassenId]);
  if (name === null || name === '') {
    return null;
  }
  return (
    <button
      type="button"
      className="kopf-bediener"
      title="Bediener wechseln"
      onClick={props.onWahl}
    >
      <em aria-hidden="true">{initialen(name)}</em>
      <span>
        <strong>{name}</strong>
        <small>Bediener wechseln</small>
      </span>
    </button>
  );
}
